import { CHUNK_DEFINITIONS, validateChunkDefinitions } from './chunks';
import { overlapsPlayer } from './collision';
import { RUNNER_CONFIG } from './config';
import { SeededRandom } from './random';
import type {
  ChunkDefinition,
  ChunkSnapshot,
  EntitySnapshot,
  GameCommand,
  GameEvent,
  GamePhase,
  GameSnapshot,
  Lane,
  ObstacleKind,
  RunnerConfig,
  RunnerSimulation,
} from './types';

interface BufferedAction {
  readonly command: Extract<GameCommand, { type: 'jump' | 'slide' | 'moveLane' }>;
  readonly expiresAt: number;
}

interface LaneTransition {
  readonly fromX: number;
  readonly targetLane: Lane;
  elapsed: number;
}

const INTRO_CHUNKS = ['welcome-coins', 'first-barrier', 'first-beam', 'choose-a-lane'] as const;

function easeInOut(value: number): number {
  return value * value * (3 - 2 * value);
}

function clampLane(value: number): Lane {
  return Math.max(-1, Math.min(1, value)) as Lane;
}

function obstacleCount(entities: readonly EntitySnapshot[], active: boolean): number {
  return entities.filter((entity) => entity.kind !== 'coin' && entity.active === active).length;
}

function coinCount(entities: readonly EntitySnapshot[], active: boolean): number {
  return entities.filter((entity) => entity.kind === 'coin' && entity.active === active).length;
}

export class SelfieSprintSimulation implements RunnerSimulation {
  private random = new SeededRandom('default');
  private readonly events: GameEvent[] = [];
  private readonly entities: EntitySnapshot[] = [];
  private readonly chunks: ChunkSnapshot[] = [];
  private readonly config: RunnerConfig;
  private nextEntityId = 1;
  private nextChunkId = 1;
  private nextChunkCenterZ = 0;
  private introIndex = 0;
  private laneTransition: LaneTransition | undefined;
  private verticalElapsed = 0;
  private verticalVelocity = 0;
  private slideHeld = false;
  private bufferedAction: BufferedAction | undefined;
  private previousDefinitionId = '';
  private readonly snapshot: GameSnapshot;

  public constructor(config: RunnerConfig = RUNNER_CONFIG) {
    this.config = config;
    const validationErrors = validateChunkDefinitions(
      CHUNK_DEFINITIONS,
      config.maxSpeed,
      config.laneChangeDuration,
    );
    if (validationErrors.length > 0) {
      throw new Error(`Invalid chunks: ${validationErrors.join('; ')}`);
    }

    this.snapshot = {
      phase: 'ready',
      seed: 'default',
      countdownRemaining: config.countdownDuration,
      elapsed: 0,
      speed: config.baseSpeed,
      distance: 0,
      coins: 0,
      score: 0,
      currentChunk: '',
      player: {
        lane: 0,
        action: 'grounded',
        x: config.laneCenters[0],
        previousX: config.laneCenters[0],
        y: 0,
        previousY: 0,
        laneChanging: false,
      },
      entities: this.entities,
      chunks: this.chunks,
      diagnostics: {
        fixedUpdates: 0,
        activeChunks: 0,
        pooledChunks: 0,
        activeObstacles: 0,
        pooledObstacles: 0,
        activeCoins: 0,
        pooledCoins: 0,
      },
    };
    this.reset('default');
  }

  public reset(seed: string): void {
    this.random = new SeededRandom(seed);
    this.snapshot.seed = seed;
    this.snapshot.phase = 'ready';
    this.resetRunState();
    this.syncDiagnostics();
  }

  public update(fixedDelta: number, commands: readonly GameCommand[]): readonly GameEvent[] {
    this.events.length = 0;
    this.snapshot.diagnostics.fixedUpdates += 1;

    this.handlePhaseCommands(commands);
    if (this.snapshot.phase === 'countdown') {
      this.snapshot.countdownRemaining = Math.max(0, this.snapshot.countdownRemaining - fixedDelta);
      if (this.snapshot.countdownRemaining === 0) this.changePhase('running');
    } else if (this.snapshot.phase === 'running') {
      this.handleGameplayCommands(commands);
      this.updatePlayer(fixedDelta);
      this.updateRun(fixedDelta);
    }

    this.syncDiagnostics();
    return this.events;
  }

  public getSnapshot(): Readonly<GameSnapshot> {
    return this.snapshot;
  }

  private resetRunState(): void {
    this.snapshot.countdownRemaining = this.config.countdownDuration;
    this.snapshot.elapsed = 0;
    this.snapshot.speed = this.config.baseSpeed;
    this.snapshot.distance = 0;
    this.snapshot.coins = 0;
    this.snapshot.score = 0;
    this.snapshot.currentChunk = '';
    this.snapshot.player.lane = 0;
    this.snapshot.player.action = 'grounded';
    this.snapshot.player.x = this.config.laneCenters[0];
    this.snapshot.player.previousX = this.config.laneCenters[0];
    this.snapshot.player.y = 0;
    this.snapshot.player.previousY = 0;
    this.snapshot.player.laneChanging = false;
    this.laneTransition = undefined;
    this.verticalElapsed = 0;
    this.verticalVelocity = 0;
    this.slideHeld = false;
    this.bufferedAction = undefined;
    this.introIndex = 0;
    this.previousDefinitionId = '';
    this.nextChunkCenterZ = -this.config.chunkLength / 2;
    for (const entity of this.entities) entity.active = false;
    for (const chunk of this.chunks) chunk.active = false;
    for (let index = 0; index < this.config.chunksAhead; index += 1) this.spawnChunk();
  }

  private handlePhaseCommands(commands: readonly GameCommand[]): void {
    for (const command of commands) {
      if (command.type === 'setSlideHeld') {
        this.slideHeld = command.held;
        continue;
      }
      if (command.type === 'pause' && this.snapshot.phase === 'running') {
        this.changePhase('paused');
        continue;
      }
      if (command.type !== 'primary') continue;
      if (this.snapshot.phase === 'ready') {
        this.resetRunState();
        this.changePhase('countdown');
      } else if (this.snapshot.phase === 'paused') {
        this.changePhase('running');
      } else if (this.snapshot.phase === 'gameOver') {
        this.resetRunState();
        this.changePhase('countdown');
      }
    }
  }

  private handleGameplayCommands(commands: readonly GameCommand[]): void {
    for (const command of commands) {
      if (command.type === 'setSlideHeld') {
        if (command.held && this.snapshot.player.action === 'grounded') {
          this.tryAction({ type: 'slide' });
        }
        continue;
      }
      if (command.type === 'jump' || command.type === 'slide' || command.type === 'moveLane') {
        if (!this.tryAction(command)) {
          this.bufferedAction = {
            command,
            expiresAt: this.snapshot.elapsed + this.config.inputBufferDuration,
          };
        }
      }
    }
  }

  private tryAction(
    command: Extract<GameCommand, { type: 'jump' | 'slide' | 'moveLane' }>,
  ): boolean {
    if (command.type === 'moveLane') {
      if (this.laneTransition !== undefined) return false;
      const targetLane = clampLane(this.snapshot.player.lane + command.direction);
      if (targetLane === this.snapshot.player.lane) return true;
      this.laneTransition = {
        fromX: this.snapshot.player.x,
        targetLane,
        elapsed: 0,
      };
      this.snapshot.player.lane = targetLane;
      this.snapshot.player.laneChanging = true;
      return true;
    }

    if (this.snapshot.player.action !== 'grounded') return false;
    this.snapshot.player.action = command.type === 'jump' ? 'jumping' : 'sliding';
    this.verticalElapsed = 0;
    if (command.type === 'jump') this.verticalVelocity = this.config.jumpVelocity;
    return true;
  }

  private updatePlayer(delta: number): void {
    const player = this.snapshot.player;
    player.previousX = player.x;
    player.previousY = player.y;

    if (this.laneTransition !== undefined) {
      this.laneTransition.elapsed += delta;
      const progress = Math.min(1, this.laneTransition.elapsed / this.config.laneChangeDuration);
      const targetX = this.config.laneCenters[this.laneTransition.targetLane];
      player.x =
        this.laneTransition.fromX + (targetX - this.laneTransition.fromX) * easeInOut(progress);
      if (progress === 1) {
        player.x = targetX;
        player.laneChanging = false;
        this.laneTransition = undefined;
      }
    }

    if (player.action === 'jumping') {
      this.verticalVelocity -= this.config.gravity * delta;
      player.y += this.verticalVelocity * delta;
      if (player.y <= 0 && this.verticalVelocity < 0) {
        player.y = 0;
        player.action = 'grounded';
        this.verticalVelocity = 0;
      }
    } else if (player.action === 'sliding') {
      this.verticalElapsed += delta;
      if (this.verticalElapsed >= this.config.slideDuration && !this.slideHeld) {
        player.action = 'grounded';
        this.verticalElapsed = 0;
      }
    } else {
      player.y = 0;
    }

    if (this.slideHeld && player.action === 'grounded') this.tryAction({ type: 'slide' });

    if (this.bufferedAction !== undefined) {
      if (this.snapshot.elapsed > this.bufferedAction.expiresAt) {
        this.bufferedAction = undefined;
      } else if (this.tryAction(this.bufferedAction.command)) {
        this.bufferedAction = undefined;
      }
    }
  }

  private updateRun(delta: number): void {
    this.snapshot.elapsed += delta;
    this.snapshot.speed = Math.min(
      this.config.maxSpeed,
      this.config.baseSpeed *
        this.config.speedGrowthFactor **
          Math.floor(this.snapshot.elapsed / this.config.speedGrowthInterval),
    );
    const travel = this.snapshot.speed * delta;
    this.snapshot.distance += travel;
    this.nextChunkCenterZ += travel;

    for (const chunk of this.chunks) {
      if (!chunk.active) continue;
      chunk.previousZ = chunk.z;
      chunk.z += travel;
      if (chunk.z > this.config.recycleBehindZ + chunk.length / 2) chunk.active = false;
    }

    for (const entity of this.entities) {
      if (!entity.active) continue;
      entity.previousZ = entity.z;
      entity.z += travel;
      if (entity.z > this.config.recycleBehindZ) {
        entity.active = false;
        continue;
      }
      if (!overlapsPlayer(entity, this.snapshot, this.config)) continue;
      if (entity.kind === 'coin') {
        entity.active = false;
        this.snapshot.coins += 1;
        this.events.push({
          type: 'coinCollected',
          entityId: entity.id,
          total: this.snapshot.coins,
        });
      } else {
        this.events.push({ type: 'collision', entityId: entity.id, obstacle: entity.kind });
        this.changePhase('gameOver');
        break;
      }
    }

    this.snapshot.score =
      Math.floor(this.snapshot.distance) + this.snapshot.coins * this.config.coinValue;
    this.ensureChunksAhead();
    this.updateCurrentChunk();
  }

  private ensureChunksAhead(): void {
    let activeChunks = this.chunks.filter((chunk) => chunk.active).length;
    while (activeChunks < this.config.chunksAhead) {
      this.spawnChunk();
      activeChunks += 1;
    }
  }

  private spawnChunk(): void {
    const definition = this.chooseChunkDefinition();
    const chunk = this.acquireChunk();
    chunk.active = true;
    chunk.definitionId = definition.id;
    chunk.z = this.nextChunkCenterZ;
    chunk.previousZ = chunk.z;
    chunk.length = definition.length;

    const nearEdgeZ = chunk.z + definition.length / 2;
    for (const placement of definition.placements) {
      const entity = this.acquireEntity(placement.kind);
      entity.active = true;
      entity.kind = placement.kind;
      entity.lane = placement.lane;
      entity.z = nearEdgeZ - placement.offset;
      entity.previousZ = entity.z;
    }

    this.events.push({
      type: 'chunkSpawned',
      chunkId: chunk.id,
      definitionId: definition.id,
    });
    this.previousDefinitionId = definition.id;
    this.nextChunkCenterZ -= definition.length;
  }

  private chooseChunkDefinition(): ChunkDefinition {
    if (this.introIndex < INTRO_CHUNKS.length) {
      const id = INTRO_CHUNKS[this.introIndex];
      this.introIndex += 1;
      const definition = CHUNK_DEFINITIONS.find((chunk) => chunk.id === id);
      if (definition === undefined) throw new Error(`Missing intro chunk: ${id}`);
      return definition;
    }

    const difficulty = this.snapshot.elapsed < 45 ? 'easy' : 'medium';
    let candidates = CHUNK_DEFINITIONS.filter(
      (chunk) => chunk.difficulty === difficulty && chunk.id !== this.previousDefinitionId,
    );
    if (candidates.length === 0) candidates = [...CHUNK_DEFINITIONS];
    const definition = candidates[this.random.integer(candidates.length)];
    if (definition === undefined) throw new Error('Chunk selection failed');
    return definition;
  }

  private acquireEntity(kind: ObstacleKind | 'coin'): EntitySnapshot {
    const reusable = this.entities.find((entity) => !entity.active && entity.kind === kind);
    if (reusable !== undefined) return reusable;
    const entity: EntitySnapshot = {
      id: this.nextEntityId,
      active: false,
      kind,
      lane: 0,
      z: 0,
      previousZ: 0,
    };
    this.nextEntityId += 1;
    this.entities.push(entity);
    return entity;
  }

  private acquireChunk(): ChunkSnapshot {
    const reusable = this.chunks.find((chunk) => !chunk.active);
    if (reusable !== undefined) return reusable;
    const chunk: ChunkSnapshot = {
      id: this.nextChunkId,
      active: false,
      definitionId: '',
      z: 0,
      previousZ: 0,
      length: this.config.chunkLength,
    };
    this.nextChunkId += 1;
    this.chunks.push(chunk);
    return chunk;
  }

  private updateCurrentChunk(): void {
    let nearest: ChunkSnapshot | undefined;
    for (const chunk of this.chunks) {
      if (!chunk.active || chunk.z > this.config.recycleBehindZ) continue;
      if (nearest === undefined || Math.abs(chunk.z) < Math.abs(nearest.z)) nearest = chunk;
    }
    this.snapshot.currentChunk = nearest?.definitionId ?? '';
  }

  private changePhase(next: GamePhase): void {
    const previous = this.snapshot.phase;
    if (previous === next) return;
    this.snapshot.phase = next;
    this.events.push({ type: 'phaseChanged', from: previous, to: next });
  }

  private syncDiagnostics(): void {
    const diagnostics = this.snapshot.diagnostics;
    diagnostics.activeChunks = this.chunks.filter((chunk) => chunk.active).length;
    diagnostics.pooledChunks = this.chunks.length - diagnostics.activeChunks;
    diagnostics.activeObstacles = obstacleCount(this.entities, true);
    diagnostics.pooledObstacles = obstacleCount(this.entities, false);
    diagnostics.activeCoins = coinCount(this.entities, true);
    diagnostics.pooledCoins = coinCount(this.entities, false);
  }
}
